export function sortOrdersItems(lineItems) {
  const typeOrder = {
    frame: 0,
    customMadeFrame: 1,
    sun: 2,
    lens: 3
  }

  function customSort(a, b) {
    const typeA = a?.item?.metadata?.type
    const typeB = b?.item?.metadata?.type

    const orderA = typeOrder[typeA] !== undefined ? typeOrder[typeA] : Infinity
    const orderB = typeOrder[typeB] !== undefined ? typeOrder[typeB] : Infinity

    return orderA - orderB
  }

  return lineItems?.sort(customSort)
}

export function isMainProduct(item) {
  return (
    item?.metadata?.type === 'frame' ||
    item?.metadata?.type === 'customMadeFrame' ||
    item?.metadata?.type === 'sun'
  )
}

export function getLegacyAttachments(orderAttachments, type) {
  const attachments = orderAttachments.filter((attachment) => attachment.reference_origin === type)

  // It's not possible to update the attachments in the import so we have to filter them by same reference and take the newest one
  // get all attachments with the same reference
  const attachmentsByReference = attachments.reduce((acc, attachment) => {
    if (!acc[attachment.reference]) {
      acc[attachment.reference] = []
    }
    acc[attachment.reference].push(attachment)
    return acc
  }, {})

  // get the newest attachment for each reference
  return Object.values(attachmentsByReference).map((attachments) =>
    attachments.reduce((acc, attachment) => {
      if (!acc || new Date(attachment.created_at) > new Date(acc.created_at)) {
        return attachment
      }
      return acc
    }, null)
  )
}

export function awaitingPayment(order) {
  return (
    order?.attachments?.some(
      (attachment) => attachment.reference_origin === 'payment--adyen-payment-link'
    ) &&
    order?.status === 'placed' &&
    !['paid', 'partially_paid', 'free'].includes(order.payment_status)
  )
}

export function calculateAmountCents(amount) {
  return Math.round((amount + Number.EPSILON) * 100)
}

// Returns the total discount on a line item as a negative float, reading from
// discount_breakdown first (storno zeroes discount_float, so the breakdown is the safe
// source). Falls back to discount_float when no breakdown exists.
// Mirrors useOrder.getOriginalDiscountFloat.
export function getLineItemDiscountFloat(lineItem) {
  const breakdown = lineItem?.discount_breakdown
  if (!breakdown || typeof breakdown !== 'object' || Object.keys(breakdown).length === 0) {
    return lineItem?.discount_float ?? 0
  }
  const totalDiscountCents = Object.values(breakdown).reduce(
    (sum, entry) => sum + Math.abs(entry.cents || 0),
    0
  )
  return -totalDiscountCents / 100
}

export function hasServiceHubAssemblyOption(lineItem) {
  return lineItem?.line_item_options?.some(
    (option) => option.sku_option?.metadata?.assembly_in_servicehub_only
  )
}

export function hasAssemblyOverAssemblyHubLocation(lineItem) {
  // origin stock location must start with SH or is Warehouse
  return (
    lineItem?.metadata?.origin_stock_location?.startsWith('SH') ||
    lineItem?.metadata?.origin_stock_location === 'WH'
  )
}

export function assembledInServiceHubCheck(lineItem, market = null) {
  // always check if the market has the assembly over service hub enabled first
  if (
    market?.metadata &&
    marketHasAssemblyOverServiceHubEnabled(market) &&
    lineItemNeedsAssembly(lineItem)
  ) {
    return true
  }

  if (lineItem.metadata?.assembly_in_service_hub_only === true) {
    return true
  }

  const isAssembledInServiceHub = hasServiceHubAssemblyOption(lineItem)

  return isAssembledInServiceHub
}

export function lineItemNeedsAssembly(lineItem) {
  return (
    ['customMadeFrame', 'frame'].includes(lineItem?.item?.metadata?.type) &&
    !lineItem?.metadata?.is_frame_only
  )
}

export function marketHasAssemblyOverServiceHubEnabled(market) {
  const isEnabled = market?.metadata?.assembly_only_in_servicehub
  if (typeof isEnabled === 'string') {
    const lowerCaseEnabled = isEnabled.toLowerCase()

    return lowerCaseEnabled === 'true' || lowerCaseEnabled === '1'
  } else if (typeof isEnabled === 'boolean') {
    return isEnabled
  }
  return false
}

/**
 * Verify if the line item needs assembly over assembly hub
 * @param {*} lineItem
 * @param {*} market
 * @returns {boolean} True if item needs assembly in service hub
 */
export function verifyAssemblyOverAssemblyHubItem(lineItem, market) {
  // Early return if not marked for service hub assembly
  if (!lineItem.metadata.assembly_in_service_hub_only) {
    return false
  }

  // Check if item has service hub assembly option
  if (hasServiceHubAssemblyOption(lineItem)) {
    return true
  }

  // if market is SH and the line item is marked for assembly in service hub, we need to return true
  if (market?.code?.startsWith('SH') && lineItem?.metadata?.assembly_in_service_hub_only === true) {
    return true
  }

  // Check if item has valid location and market allows service hub assembly
  return (
    hasAssemblyOverAssemblyHubLocation(lineItem) && marketHasAssemblyOverServiceHubEnabled(market)
  )
}

// Special case for spare parts (Services) that are assembled in the service hub but send from the store to the service hub
// therefore the stock location should be always the store
export function serviceOverServiceHubCheck(item) {
  const key = 'assembly_in_servicehub_only'
  if (item?.metadata?.type === 'spare_part') {
    if (item?.item?.metadata && item?.item?.metadata[key]) {
      return true
    } else if (item?.sku?.metadata && item?.sku?.metadata[key]) {
      return true
    } else if (item?.metadata[key]) {
      return true
    }
  }

  return false
}

export function sparePartAssemblyInServiceHubCheck(lineItem, market) {
  if (lineItem?.metadata?.type === 'spare_part' && market?.code?.startsWith('SH')) {
    return true
  }
  return false
}

export function getServiceHubStockLocation(countryCode) {
  countryCode = countryCode ? countryCode.toUpperCase() : null
  if (['CH', 'LI'].includes(countryCode)) {
    return 'SHCH'
  } else if (['AT'].includes(countryCode)) {
    return 'SHAT'
  } else {
    return 'SHDE'
  }
}

export function showAssemblyOverServiceHub(lineItem) {
  return assembledInServiceHubCheck(lineItem) || serviceOverServiceHubCheck(lineItem)
}

/**
 * Use this Function on places where it's important that the tax region on the order must not change
 *
 * E.g. On Delivery Method Change if order is already placed. Here the tax is already set on approve and must not change afterwards.
 *
 * @param {*} orderCountryCode
 * @param {*} shippingCountryCode
 * @returns
 */
export function countryCodeIsInSameTaxRegion(orderCountryCode, shippingCountryCode) {
  const CHCodes = ['CH', 'LI']
  // if order is in CH or LI, then shipping must be in CH or LI
  if (CHCodes.includes(orderCountryCode.toUpperCase())) {
    return CHCodes.includes(shippingCountryCode.toUpperCase())
  } else {
    // if order is not in CH or LI, then shipping must be in same country
    return orderCountryCode.toUpperCase() === shippingCountryCode.toUpperCase()
  }
}

export function formatCurrency(amount, currencyCode) {
  if (amount == null || amount == undefined) amount = 0
  switch (currencyCode) {
    case 'CHF':
      return (
        'CHF ' +
        amount.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      )
    case 'EUR':
      return (
        amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
        ' €'
      )
    default:
      return amount.toFixed(2)
  }
}

// After-Sales (intolerance / VIUplus insurance) gift cards are produced by the After Sales
// process and may only ever be attached to a cart through it — never added manually by staff.
// They are identified by their reference prefix (same signal as cartHasAfterSalesGiftCard).
export function isAfterSalesGiftCard(reference) {
  return reference?.startsWith('AS/INTOLERANCE') || reference?.startsWith('AS/INSURANCE')
}
